<script setup lang="ts">
    import ChevronDarkIcon from "@/assets/arrow_down_dark.svg";
    import ChevronBlueIcon from "@/assets/dropdown-arrow-blue.svg";
    import { useStore } from "@/store";
    import { CONFIG_PROVIDE_NAME, ConfigType } from "@/store/modules/types/configModuleTypes";
    // Vue collapse transition har ingen typning
    // @ts-ignore
    import { CollapseTransition } from "@ivanv/vue-collapse-transition";
    import { computed, inject, provide, ref, toRefs, watch, getCurrentInstance } from "vue";

    const store = useStore();

    const icons = {
        boxed: ChevronDarkIcon,
        default: ChevronBlueIcon,
    };

    const instance = getCurrentInstance();

    interface Props {
        /** Namn på sektionen. Används för hämta inställningar */
        name: string;
        heading?: string;
        numbered?: boolean;
        number?: string | number;
        grid?: boolean;
        collapsible?: boolean;
        expanded?: boolean;
        /**
         * Namn som läggs på i slutet av CONFIG_PROVIDE_NAME. Används för att kunna sätta fler provide i samma komponent
         * och att kunna hämta en specifik samling inställningar.
         * SKA GENERELLT INTE ANVÄNDAS (med undantag de platser som använder FormulateInput och har flera sektioner,
         * t.ex Opjournalen). Använd K-formulärkomponenterna istället i nya komponenter.
         *
         * KAN TAS BORT NÄr VI INTE LÄNGER HAR <FormulateInput>-komponenter, utan helt gått över till
         * K-formulärkomponenter eller de äldre InputWrapper
         */
        customConfigProvideSuffix?: string;
        /**
         * Placering av chevron till vänster eller höger om annat content i heading
         */
        chevronPlacement?: "left" | "right";
        variant?: "default" | "boxed";
    }

    const props = withDefaults(defineProps<Props>(), {
        numbered: true,
        grid: false,
        collapsible: true,
        expanded: true,
        chevronPlacement: "right",
        variant: "default",
    });

    const { name, customConfigProvideSuffix } = toRefs(props);

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>(CONFIG_PROVIDE_NAME);

    /**
     * Overridar CONFIG_PROVIDE_NAME provide med inställningar baserat på custom namn. Alla child av KSection
     * kommer istället få det moduleName och subModuleName som är satt här.
     *
     * KAN TAS BORT NÄr VI INTE LÄNGER HAR <FormulateInput>-komponenter, utan helt gått över till K-formulärkomponenter
     * eller de äldre InputWrapper
     */
    if (customConfigProvideSuffix?.value) {
        const customConfigNames = inject<{
            moduleName: string;
            subModuleName: string;
        }>(`${CONFIG_PROVIDE_NAME}-${customConfigProvideSuffix.value}`);

        provide(CONFIG_PROVIDE_NAME, {
            moduleName: configNames?.moduleName,
            subModuleName: customConfigNames?.subModuleName,
        });
    }

    const show = ref(props.expanded ?? true);

    const toggle = () => {
        if (props.collapsible) {
            show.value = !show.value;
        }
    };

    watch(
        () => props.expanded,
        (newVal) => {
            show.value = newVal;
        }
    );

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
            type: ConfigType.Section,
        })
    );

    const index = computed(() =>
        store.getters["config/visibleSectionIndex"]({
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
        })
    );

    const formErrorFields = inject<string[]>("formErrorFields", []);

    // Samlar dynamiskt in fältnamn från slot-barn
    const slotFieldNames = computed(() => {
        const defaultSlot = instance?.proxy?.$slots?.default;
        if (!defaultSlot || !Array.isArray(defaultSlot)) return [];
        const fieldNames: string[] = [];

        const extractNames = (vnodes: any[]) => {
            vnodes.forEach((vnode: any) => {
                // Kontrollera om taggen är KGroup
                if (vnode.tag && typeof vnode.tag === "string" && vnode.tag.includes("KGroup")) {
                    // KGroup barn kan vara i componentOptions.children:

                    const groupChildren = vnode.componentOptions?.children || [];
                    extractNames(groupChildren);
                } else {
                    const foundFieldName = vnode.componentOptions?.propsData?.name;
                    if (typeof foundFieldName === "string") {
                        fieldNames.push(foundFieldName);
                    }
                }
            });
        };

        extractNames(defaultSlot[0]?.children || []);
        return fieldNames;
    });

    // Övervaka formErrorFields och expandera sektionen om något slot-fält har ett fel
    watch(formErrorFields, (newErrorFields: string[]) => {
        if (slotFieldNames.value.some((field: string) => newErrorFields.includes(field))) {
            show.value = true;
        }
    });
</script>

<template>
    <div v-if="shouldBeVisible" :class="['k-section', `variant-${variant}`, { show }]">
        <div
            v-if="collapsible || numbered || heading || $slots.heading"
            class="header"
            :class="{ 'not-numbered': !numbered }"
        >
            <h3>
                <span v-if="numbered" class="circle">
                    <span class="number">{{ number ? number : index }}</span>
                </span>
                <span :class="{ toggle: collapsible }">
                    <img
                        v-if="collapsible && chevronPlacement === 'left'"
                        :src="icons[variant]"
                        :class="['chevron', chevronPlacement, { rotate: show }]"
                        :alt="show ? 'Expandera' : 'Minimera'"
                        @click.stop.prevent="toggle"
                    />

                    <span class="toggle-content" @click.stop.prevent="toggle">
                        <template v-if="heading">{{ heading }}</template>
                        <template v-else-if="$slots.heading">
                            <slot name="heading" />
                        </template>
                    </span>

                    <img
                        v-if="collapsible && chevronPlacement === 'right'"
                        :src="icons[variant]"
                        :class="['chevron', chevronPlacement, { rotate: show }]"
                        :alt="show ? 'Expandera' : 'Minimera'"
                        @click.stop.prevent="toggle"
                    />
                </span>
            </h3>
        </div>
        <div :class="['outer', { numbered }]">
            <div v-if="numbered" class="line" />
            <collapse-transition>
                <div class="content" :class="['inner', { numbered, grid: grid }]" v-show="show">
                    <slot></slot>
                </div>
            </collapse-transition>
            <!-- Content som kommer renderas om sektionen är collapsad -->
            <collapse-transition v-if="$slots.collapsedContent">
                <div v-show="!show" class="inner">
                    <div v-if="numbered" class="line" />
                    <slot name="collapsedContent" />
                </div>
            </collapse-transition>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @use "sass:math";

    @import "@/style/components";
    @import "@/style/variables";

    .k-section {
        max-width: 100%;

        .header {
            padding-block: 1rem;

            &:not(.not-numbered) {
                padding-inline: 1rem;
            }

            h3 {
                display: flex;
                align-items: center;
                margin-bottom: 0;
            }

            .heading {
                display: flex;
                align-items: center;
                gap: 1rem;
            }

            .toggle {
                width: 100%;
                display: flex;
                cursor: pointer;
                gap: 1rem;
                align-items: center;
            }
        }

        &.variant-default {
            .header {
                .circle {
                    display: inline-block;
                    height: $k-section-circle-size;
                    width: $k-section-circle-size;
                    background-color: #277692;
                    border-radius: 50%;
                    position: relative;
                    left: math.div($k-section-circle-size, 2) * -1;

                    .number {
                        display: grid;
                        place-items: center;
                        height: 100%;
                        width: 100%;
                        color: #ffffff;
                        font-family: Roboto;
                        font-size: 1rem;
                        letter-spacing: 0;
                        text-align: center;
                    }
                }
            }
        }

        &.variant-boxed {
            .header {
                // Görs för att tillåta att toggle tar hela höjden och hel ytan blir klickbar
                padding: 0;

                h3 {
                    font-size: 1rem;
                    line-height: 1;

                    .toggle {
                        color: $color-text-black;
                        border: $border-bright-blue;
                        border-radius: $border-radius-max;
                        background-color: $color-light;
                        padding: 1rem;
                    }
                }
            }

            .content {
                padding: 1rem;
                padding-top: 0;
                border: $border-bright-blue;
                border-radius: $border-radius-max;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-top: 0;
                background-color: $color-light;
            }

            &.show {
                .header {
                    h3 {
                        .toggle {
                            border-bottom-left-radius: 0;
                            border-bottom-right-radius: 0;
                            border-bottom: 0;
                        }
                    }
                }
            }
        }

        .numbered {
            position: relative;
            min-height: 1rem;

            .line {
                position: absolute;
                left: 0;
                height: 100%;
                border-left: 1px solid #8ebaca;
                width: 1px;
            }

            .numbered {
                padding-left: $k-section-left-padding;
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
            }
        }

        @media print {
            .line,
            .chevron {
                display: none !important;
            }

            .inner.numbered {
                padding-left: 0 !important;
            }

            .header {
                padding: 0.2rem 0 !important;
            }

            .numbered {
                padding-top: 0.15rem !important;
                padding-bottom: 0.15rem !important;
            }
        }

        img {
            &.rotate {
                transform: rotate(180deg);
            }
        }
    }
</style>
